import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { API_URL } from '../config';
import { getAuthHeader } from '../utils/auth';
import {
  Eye, EyeOff, PauseCircle, PlayCircle,
  FileSpreadsheet, BarChart2, Users, Search, ChevronDown, ChevronUp,
  Clock, CheckCircle2, XCircle, AlertTriangle, Settings, X, Link2, ExternalLink
} from 'lucide-react';

const StatusBadge = ({ is_active, is_paused }) => {
  if (is_paused) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-500/20 text-orange-400 border border-orange-500/20">
      <PauseCircle className="w-3 h-3" /> PAUSED
    </span>
  );
  if (!is_active) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400 border border-red-500/20">
      <XCircle className="w-3 h-3" /> DISABLED
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/20">
      <CheckCircle2 className="w-3 h-3" /> ACTIVE
    </span>
  );
};

const Toggle = ({ enabled, onChange, label, icon: Icon, color = 'green', disabled = false }) => {
  const colors = {
    green: enabled ? 'bg-[#22c55e]' : 'bg-zinc-700',
    blue: enabled ? 'bg-[#6366f1]' : 'bg-zinc-700',
    orange: enabled ? 'bg-orange-500' : 'bg-zinc-700',
  };
  return (
    <button
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-colors text-xs font-medium ${
        disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:border-white/20'
      } ${enabled ? 'border-white/10 bg-white/5' : 'border-white/5 bg-black/20'}`}
    >
      {Icon && <Icon className={`w-3.5 h-3.5 ${enabled ? 'text-white' : 'text-zinc-500'}`} />}
      <span className={enabled ? 'text-white' : 'text-zinc-500'}>{label}</span>
      <div className={`relative w-8 h-4 rounded-full transition-colors ${colors[color]}`}>
        <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </div>
    </button>
  );
};

const PauseModal = ({ client, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="bg-[#09090B] rounded-2xl border border-orange-500/30 shadow-2xl w-full max-w-md relative z-10 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
            <PauseCircle className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h3 className="font-bold text-white">Pause Portal Access</h3>
            <p className="text-xs text-zinc-500">{client.name} · {client.email}</p>
          </div>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          This will immediately prevent <strong className="text-white">{client.name}</strong> from accessing their portal. You can resume anytime.
        </p>
        <div className="mb-4">
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
            Reason (shown to client)
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Payment overdue, Account under review..."
            rows={3}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-orange-500/50 resize-none"
          />
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 text-zinc-400 text-sm font-medium hover:bg-white/10 transition-colors">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            className="flex-1 px-4 py-2.5 rounded-xl bg-orange-500/20 text-orange-400 border border-orange-500/30 text-sm font-bold hover:bg-orange-500/30 transition-colors"
          >
            Pause Access
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- Unified Client Management Drawer ----
const ManageDrawer = ({ client, onClose, onUpdate, isSaving, formLinkCount = 0 }) => {
  const access = client.portal_access;
  const [localAccess, setLocalAccess] = useState({ ...access });
  const [changed, setChanged] = useState(false);

  const set = (k, v) => {
    setLocalAccess(prev => ({ ...prev, [k]: v }));
    setChanged(true);
  };

  const handleSave = () => {
    onUpdate(client.id, localAccess);
    setChanged(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-end">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="bg-[#09090B] border-l border-white/10 shadow-2xl w-full sm:w-96 h-full overflow-y-auto relative z-10 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#09090B] z-10">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm ${
              access.is_paused ? 'bg-orange-500/10 text-orange-400' : 'bg-[#22c55e]/10 text-[#22c55e]'
            }`}>
              {client.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="font-bold text-white text-sm">{client.name}</h3>
              <p className="text-xs text-zinc-500 font-mono truncate">{client.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 p-5 space-y-6">
          {/* Account Info */}
          <div>
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Account Info</p>
            <div className="bg-black/20 rounded-xl p-4 space-y-2 text-sm">
              {client.company_name && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">Company</span>
                  <span className="text-white">{client.company_name}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-zinc-500">Platform</span>
                <span className="text-white capitalize">{client.platform || 'N/A'}</span>
              </div>
              {client.assigned_to && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">Marketer</span>
                  <span className="text-white">{client.assigned_to}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-zinc-500">Status</span>
                <StatusBadge is_active={client.is_active} is_paused={localAccess.is_paused} />
              </div>
              {client.last_login && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">Last Login</span>
                  <span className="text-zinc-400 text-xs font-mono">{new Date(client.last_login).toLocaleDateString('en-IN')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Portal Features */}
          <div>
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Portal Features</p>
            <div className="space-y-2">
              <Toggle
                enabled={localAccess.sheet_enabled}
                onChange={v => set('sheet_enabled', v)}
                label="Ad Reports / Sheet View"
                icon={FileSpreadsheet}
                color="blue"
                disabled={localAccess.is_paused}
              />
              <Toggle
                enabled={localAccess.crm_enabled}
                onChange={v => set('crm_enabled', v)}
                label="Callboard CRM"
                icon={BarChart2}
                color="green"
                disabled={localAccess.is_paused}
              />
              {localAccess.crm_enabled && (
                <Toggle
                  enabled={!localAccess.crm_read_only}
                  onChange={v => set('crm_read_only', !v)}
                  label="Can Add / Edit Leads"
                  icon={localAccess.crm_read_only ? EyeOff : Eye}
                  color="green"
                />
              )}
            </div>
          </div>

          {/* Access Control */}
          <div>
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Access Control</p>
            {localAccess.is_paused ? (
              <div className="space-y-3">
                {localAccess.pause_reason && (
                  <div className="flex items-start gap-2 bg-orange-500/10 border border-orange-500/20 rounded-xl p-3">
                    <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-orange-300">{localAccess.pause_reason}</p>
                  </div>
                )}
                <button
                  onClick={() => set('is_paused', false)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#22c55e]/10 text-[#22c55e] border border-[#22c55e]/20 text-sm font-bold hover:bg-[#22c55e]/20"
                >
                  <PlayCircle className="w-4 h-4" />Resume Access
                </button>
              </div>
            ) : (
              <button
                onClick={() => set('is_paused', true)}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-orange-500/10 text-orange-400 border border-orange-500/20 text-sm font-bold hover:bg-orange-500/20"
              >
                <PauseCircle className="w-4 h-4" />Pause Access
              </button>
            )}
          </div>

          {/* Form Links */}
          {localAccess.crm_enabled && (
            <div>
              <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Meta Form Links</p>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-black/20 border border-white/5 rounded-xl px-3 py-2">
                  <Link2 className={`w-3.5 h-3.5 ${formLinkCount ? 'text-[#6366f1]' : 'text-zinc-600'}`} />
                  <span className={`text-xs font-mono font-bold ${formLinkCount ? 'text-[#6366f1]' : 'text-zinc-600'}`}>
                    {formLinkCount} form{formLinkCount !== 1 ? 's' : ''} linked
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Last updated */}
          <div className="flex items-center gap-2">
            <Clock className="w-3 h-3 text-zinc-600" />
            <span className="text-[10px] text-zinc-600">
              Updated: {access.updated_at ? new Date(access.updated_at).toLocaleString('en-IN') : 'Never'}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-white/10 sticky bottom-0 bg-[#09090B] flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-zinc-400 text-sm font-medium hover:bg-white/10">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!changed || isSaving}
            className="flex-1 py-2.5 rounded-xl bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30 text-sm font-bold hover:bg-[#22c55e]/30 disabled:opacity-40"
          >
            {isSaving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ClientAccessManager({ onManageForms }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [pauseModal, setPauseModal] = useState(null);
  const [saving, setSaving] = useState({});
  const [manageDrawer, setManageDrawer] = useState(null);
  const [formLinkCounts, setFormLinkCounts] = useState({});

  useEffect(() => {
    fetchClients();
    fetchFormLinkCounts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/portal-access`, { headers: getAuthHeader() });
      if (res.ok) {
        const d = await res.json();
        setClients(d.clients || []);
      }
    } catch (e) {
      toast.error('Failed to load clients');
    }
    setLoading(false);
  };

  const fetchFormLinkCounts = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/crm/form-links`, { headers: getAuthHeader() });
      if (res.ok) {
        const d = await res.json();
        const counts = {};
        (d.links || []).forEach(l => {
          counts[l.client_id] = (counts[l.client_id] || 0) + 1;
        });
        setFormLinkCounts(counts);
      }
    } catch {
      // non-critical, ignore
    }
  };

  const updateAccess = async (clientId, patch) => {
    setSaving(prev => ({ ...prev, [clientId]: true }));
    try {
      const res = await fetch(`${API_URL}/admin/portal-access/${clientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        setClients(prev => prev.map(c => {
          if (c.id !== clientId) return c;
          return { ...c, portal_access: { ...c.portal_access, ...patch } };
        }));
        toast.success('Access updated');
      } else {
        toast.error('Failed to update');
      }
    } catch {
      toast.error('Network error');
    }
    setSaving(prev => ({ ...prev, [clientId]: false }));
  };

  const handlePause = (client) => {
    if (client.portal_access.is_paused) {
      // Resume immediately
      updateAccess(client.id, { is_paused: false, pause_reason: null });
    } else {
      setPauseModal(client);
    }
  };

  const confirmPause = async (reason) => {
    if (!pauseModal) return;
    await updateAccess(pauseModal.id, { is_paused: true, pause_reason: reason || 'Access paused by admin' });
    setPauseModal(null);
  };

  const filtered = clients.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase()) ||
    (c.company_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: clients.length,
    paused: clients.filter(c => c.portal_access.is_paused).length,
    crm_enabled: clients.filter(c => c.portal_access.crm_enabled).length,
    sheet_enabled: clients.filter(c => c.portal_access.sheet_enabled).length,
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1,2,3].map(i => (
          <div key={i} className="animate-pulse bg-zinc-800/60 rounded-2xl h-20" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Clients', value: stats.total, icon: Users, color: 'text-white' },
          { label: 'CRM Enabled', value: stats.crm_enabled, icon: BarChart2, color: 'text-[#22c55e]' },
          { label: 'Sheet Enabled', value: stats.sheet_enabled, icon: FileSpreadsheet, color: 'text-[#6366f1]' },
          { label: 'Paused', value: stats.paused, icon: PauseCircle, color: 'text-orange-400' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-[#09090B] border border-white/5 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon className={`w-4 h-4 ${color}`} />
              <span className="text-xs text-zinc-500">{label}</span>
            </div>
            <p className={`text-2xl font-bold font-mono ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search clients..."
          className="w-full bg-[#09090B] border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/20"
        />
      </div>

      {/* Client List */}
      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="text-center py-12 text-zinc-500">No clients found</div>
        )}
        {filtered.map(client => {
          const access = client.portal_access;
          const isExpanded = expanded[client.id];
          const isSaving = saving[client.id];

          return (
            <div
              key={client.id}
              className={`bg-[#09090B] border rounded-2xl overflow-hidden transition-colors ${
                access.is_paused ? 'border-orange-500/30' : 'border-white/5'
              }`}
            >
              {/* Row */}
              <div className="flex items-center gap-4 p-4">
                {/* Avatar */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                  access.is_paused ? 'bg-orange-500/10 text-orange-400' : 'bg-[#22c55e]/10 text-[#22c55e]'
                }`}>
                  {client.name.charAt(0).toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white text-sm">{client.name}</span>
                    <StatusBadge is_active={client.is_active} is_paused={access.is_paused} />
                  </div>
                  <p className="text-xs text-zinc-500 font-mono mt-0.5 truncate">
                    {client.email} {client.company_name ? `· ${client.company_name}` : ''}
                  </p>
                  {client.last_login && (
                    <p className="text-[10px] text-zinc-600 mt-0.5">
                      Last login: {new Date(client.last_login).toLocaleDateString('en-IN')}
                    </p>
                  )}
                </div>

                {/* Quick toggles */}
                <div className="hidden md:flex items-center gap-2">
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-black/20 border border-white/5">
                    <FileSpreadsheet className={`w-3.5 h-3.5 ${access.sheet_enabled ? 'text-[#6366f1]' : 'text-zinc-600'}`} />
                    <span className={`text-[10px] font-semibold ${access.sheet_enabled ? 'text-[#6366f1]' : 'text-zinc-600'}`}>
                      {access.sheet_enabled ? 'Sheet ON' : 'Sheet OFF'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-black/20 border border-white/5">
                    <BarChart2 className={`w-3.5 h-3.5 ${access.crm_enabled ? 'text-[#22c55e]' : 'text-zinc-600'}`} />
                    <span className={`text-[10px] font-semibold ${access.crm_enabled ? 'text-[#22c55e]' : 'text-zinc-600'}`}>
                      {access.crm_enabled ? 'CRM ON' : 'CRM OFF'}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setManageDrawer(client)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-[#6366f1]/10 text-[#6366f1] hover:bg-[#6366f1]/20 border border-[#6366f1]/20 transition-colors"
                  >
                    <Settings className="w-3 h-3" />Manage
                  </button>
                  <button
                    onClick={() => handlePause(client)}
                    disabled={isSaving}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-colors ${
                      access.is_paused
                        ? 'bg-[#22c55e]/10 text-[#22c55e] hover:bg-[#22c55e]/20 border border-[#22c55e]/20'
                        : 'bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 border border-orange-500/20'
                    }`}
                  >
                    {access.is_paused ? <><PlayCircle className="w-3 h-3 inline mr-1" />Resume</> : <><PauseCircle className="w-3 h-3 inline mr-1" />Pause</>}
                  </button>
                  <button
                    onClick={() => setExpanded(prev => ({ ...prev, [client.id]: !prev[client.id] }))}
                    className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                  >
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Expanded Controls */}
              {isExpanded && (
                <div className="border-t border-white/5 p-4 bg-black/20 space-y-4">
                  {access.is_paused && access.pause_reason && (
                    <div className="flex items-start gap-2 bg-orange-500/10 border border-orange-500/20 rounded-xl p-3">
                      <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-orange-300">{access.pause_reason}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Portal Features</p>
                    <div className="flex flex-wrap gap-2">
                      <Toggle
                        enabled={access.sheet_enabled}
                        onChange={v => updateAccess(client.id, { sheet_enabled: v })}
                        label="Ad Reports / Sheet View"
                        icon={FileSpreadsheet}
                        color="blue"
                        disabled={isSaving || access.is_paused}
                      />
                      <Toggle
                        enabled={access.crm_enabled}
                        onChange={v => updateAccess(client.id, { crm_enabled: v })}
                        label="Callboard CRM"
                        icon={BarChart2}
                        color="green"
                        disabled={isSaving || access.is_paused}
                      />
                    </div>
                  </div>

                  {access.crm_enabled && (
                    <div>
                      <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">CRM Permissions</p>
                      <div className="flex flex-wrap gap-2">
                        <Toggle
                          enabled={!access.crm_read_only}
                          onChange={v => updateAccess(client.id, { crm_read_only: !v })}
                          label="Can Add / Edit Leads"
                          icon={access.crm_read_only ? EyeOff : Eye}
                          color="green"
                          disabled={isSaving}
                        />
                      </div>
                      {access.crm_read_only && (
                        <p className="text-[10px] text-zinc-600 mt-2">
                          Client can view leads but cannot add, edit, or delete.
                        </p>
                      )}
                    </div>
                  )}

                  {access.crm_enabled && (
                    <div>
                      <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Meta Form Links</p>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-black/20 border border-white/5 rounded-xl px-3 py-2">
                          <Link2 className={`w-3.5 h-3.5 ${formLinkCounts[client.id] ? 'text-[#6366f1]' : 'text-zinc-600'}`} />
                          <span className={`text-xs font-mono font-bold ${formLinkCounts[client.id] ? 'text-[#6366f1]' : 'text-zinc-600'}`}>
                            {formLinkCounts[client.id] || 0} form{(formLinkCounts[client.id] || 0) !== 1 ? 's' : ''} linked
                          </span>
                        </div>
                        {onManageForms && (
                          <button
                            onClick={() => onManageForms(client)}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#6366f1]/10 text-[#6366f1] border border-[#6366f1]/20 text-xs font-bold hover:bg-[#6366f1]/20 transition-colors"
                          >
                            <ExternalLink className="w-3 h-3" />Manage Forms
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-1">
                    <Clock className="w-3 h-3 text-zinc-600" />
                    <span className="text-[10px] text-zinc-600">
                      Last updated: {access.updated_at ? new Date(access.updated_at).toLocaleString('en-IN') : 'Never'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {pauseModal && (
        <PauseModal
          client={pauseModal}
          onClose={() => setPauseModal(null)}
          onConfirm={confirmPause}
        />
      )}

      {manageDrawer && (
        <ManageDrawer
          client={manageDrawer}
          onClose={() => setManageDrawer(null)}
          onUpdate={(clientId, patch) => {
            updateAccess(clientId, patch);
            setManageDrawer(null);
          }}
          isSaving={saving[manageDrawer.id]}
          formLinkCount={formLinkCounts[manageDrawer.id] || 0}
        />
      )}
    </div>
  );
}
